<template>
  <div class="container-fluid py-4">
    <ToastPrime position="bottom-left"/>
    
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <div v-else-if="userProfile" class="row g-4">
      <!-- Left Column: Photo & Password -->
      <div class="col-lg-4">
        <!-- Photo Card -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-body text-center p-4">
            <h6 class="fw-bold mb-3 text-start">Profile Photo</h6>

            <div class="position-relative d-inline-block mb-3">
              <div
                class="avatar-wrapper rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center mx-auto"
                style="width: 15rem; height: 15rem; border: 4px solid #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                <img
v-if="previewUrl || userImage" :src="previewUrl || userImage" alt="Profile"
                  class="w-100 h-100 object-fit-cover" @error="handleImageError" />
                <i v-else class="pi pi-user text-secondary" style="font-size: 3rem; opacity: 0.3;"></i>
              </div>
              <label
for="photo-upload" class="btn btn-sm btn-primary position-absolute rounded-circle p-2 shadow-sm"
                style="width: 45px; height: 45px; display: flex; align-items: center; justify-content: center; bottom: 13px; right: 15px;">
                <i class="pi pi-camera" style="font-size: 1.5rem;"></i>
              </label>
              <input id="photo-upload" type="file" class="d-none" accept="image/*" @change="handleFileSelect" />
            </div>

            <h5 class="mb-1">{{ userProfile.name }}</h5>
            <p class="text-muted mb-2">{{ userProfile.username }}</p>
            <span 
              class="badge" 
              :class="{
                'bg-success': userProfile.status === 'active',
                'bg-danger': userProfile.status === 'inactive',
                'bg-warning': userProfile.status === 'pending',
                'bg-secondary': !['active', 'inactive', 'pending'].includes(userProfile.status)
              }"
            >
              {{ userProfile.status }}
            </span>

            <p class="text-xs text-muted mb-0 mt-3">Allowed *.jpeg, *.jpg, *.png</p>
            <p class="text-xs text-muted">Max size of 2 MB</p>
            <small v-if="errors.image" class="text-danger d-block">{{ errors.image }}</small>
          </div>
        </div>

        <!-- Password Card -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-4">
            <h6 class="fw-bold mb-3"><i class="pi pi-lock me-2 text-primary"></i>Security</h6>
            <div class="mb-3">
              <label class="form-label text-xs fw-bold text-muted text-uppercase">New Password</label>
              <div class="password-input-wrapper">
                <InputText
v-model="formData.password" type="password" placeholder="Leave blank to keep current"
                  :class="{ 'p-invalid': errors.password }" class="w-100" name="password" />
                <button type="button" class="password-toggle-btn" @click="togglePasswordVisibility('password')">
                  <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
              <small v-if="errors.password" class="text-danger">{{ errors.password }}</small>
              <!-- Password Policy Checklist -->
              <div v-if="formData.password.length > 0" class="mt-2">
                <small class="text-muted d-block mb-1">Password requirements:</small>
                <small :class="passwordPolicy.minLength ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.minLength ? 'fas fa-check' : 'fas fa-times'"></i> Min. 8 characters
                </small>
                <small :class="passwordPolicy.hasUppercase ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasUppercase ? 'fas fa-check' : 'fas fa-times'"></i> Uppercase letter (A-Z)
                </small>
                <small :class="passwordPolicy.hasLowercase ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasLowercase ? 'fas fa-check' : 'fas fa-times'"></i> Lowercase letter (a-z)
                </small>
                <small :class="passwordPolicy.hasNumber ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasNumber ? 'fas fa-check' : 'fas fa-times'"></i> Number (0-9)
                </small>
                <small :class="passwordPolicy.hasSymbol ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasSymbol ? 'fas fa-check' : 'fas fa-times'"></i> Symbol (!@#$%^&* etc.)
                </small>
              </div>
            </div>
            <div class="mb-0">
              <label class="form-label text-xs fw-bold text-muted text-uppercase">Confirm Password</label>
              <div class="password-input-wrapper">
                <InputText
v-model="formData.password_confirmation" type="password" placeholder="Leave blank to keep current"
                  :class="{ 'p-invalid': errors.password_confirmation }" class="w-100" name="password_confirmation" />
                <button type="button" class="password-toggle-btn" @click="togglePasswordVisibility('password_confirmation')">
                  <i :class="showPasswordConfirmation ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
              <small v-if="errors.password_confirmation" class="text-danger">{{ errors.password_confirmation }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: User Details -->
      <div class="col-lg-8">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h6 class="fw-bold mb-0"><i class="pi pi-id-card me-2 text-primary"></i>Account Information</h6>
              <button type="button" class="mx-1 mb-0 btn btn-sm btn-primary" :disabled="updateLoading" @click="updateProfile">
                <span v-if="!updateLoading">Save Changes</span>
                <span v-else><i class="pi pi-spin pi-spinner"></i> Saving...</span>
              </button>
            </div>

            <div class="row g-4">
              <!-- Identity Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Identity</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Username</label>
                    <InputText v-tooltip.top="'Username cannot be changed'" :model-value="userProfile.username" disabled fluid />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Full Name <span class="text-danger">*</span></label>
                    <InputText v-model="formData.name" placeholder="Enter full name" :invalid="!!errors.name" fluid />
                    <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
                  </div>
                </div>
              </div>

              <!-- Contact Details Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Contact Details</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Email Address <span class="text-danger">*</span></label>
                    <InputText v-model="formData.email" type="email" placeholder="name@company.com" :invalid="!!errors.email" fluid />
                    <small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Phone Number</label>
                    <InputText v-model="formData.phone" placeholder="+62..." :invalid="!!errors.phone" fluid />
                    <small v-if="errors.phone" class="text-danger">{{ errors.phone }}</small>
                  </div>
                </div>
              </div>

              <!-- Organization Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Organization</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Company Code</label>
                    <InputText :model-value="userProfile.company_code" disabled fluid />
                  </div>
                  <!-- <div class="col-md-6">
                    <label class="form-label fw-semibold">Department</label>
                    <InputText :model-value="formData.department" disabled fluid />
                  </div> -->
                  <div class="col-md-6">
                    <label for="profile-section" class="form-label fw-semibold">Department</label>
                    <MultiSelect id="profile-section" v-model="formData.section" :options="sectionOptions" option-label="label"
                      option-value="value" filter placeholder="Search Section (min. 2 characters)" class="w-100" display="chip" show-clear
                      :loading="sectionLoading" @show="loadSectionOptions" @filter="searchSectionOptions" />
                    <small v-if="errors.sections" class="text-danger">{{ errors.sections }}</small>
                  </div>
                  <div class="col-md-6">
                    <label for="profile-sub-section" class="form-label fw-semibold">Section</label>
                    <MultiSelect id="profile-sub-section" v-model="formData.sub_section" :options="subSectionOptions" option-label="label"
                      option-value="value" filter placeholder="Search Sub Section (min. 2 characters)" class="w-100" display="chip" show-clear
                      :loading="subSectionLoading" @show="loadSubSectionOptions" @filter="searchSubSectionOptions" />
                    <small v-if="errors.sub_sections" class="text-danger">{{ errors.sub_sections }}</small>
                  </div>
                </div>
              </div>

              <!-- User Roles -->
              <div v-if="userProfile.user_roles && userProfile.user_roles.length > 0" class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">User Roles</h6>
                <div class="d-flex flex-wrap gap-2">
                  <span v-for="role in userProfile.user_roles" :key="role.id" class="badge bg-primary">
                    {{ role.name }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from "@/services/authService";
import api from "@/helpers/api";
import config from "@/configs/config";
import generalMasterDataService from '@/services/generalMasterData/generalMasterDataService';
import setMaxSize from "@/helpers/setMaxSize";

export default {
  name: "Profile",
  data() {
    return {
      userProfile: null,
      loading: true,
      error: null,
      selectedFile: null,
      previewUrl: null,
      updateLoading: false,
      showPassword: false,
      showPasswordConfirmation: false,
      formData: {
        name: '',
        email: '',
        phone: '',
        department: '',
        section: [],
        sub_section: [],
        password: '',
        password_confirmation: ''
      },
      sectionOptions: [],
      subSectionOptions: [],
      allSectionOptions: [],
      allSubSectionOptions: [],
      sectionLoading: false,
      subSectionLoading: false,
      errors: {},
      maxValidation: {
        size: 2000000,
        text: "Max Size 2000 KB"
      },
    };
  },
  computed: {
    userImage() {
      const user_image = this.userProfile?.image;
      return user_image ? `${config.storageUrl}/${user_image}` : "";
    },
    passwordPolicy() {
      const p = this.formData.password;
      return {
        minLength: p.length >= 8,
        hasUppercase: /[A-Z]/.test(p),
        hasLowercase: /[a-z]/.test(p),
        hasNumber: /[0-9]/.test(p),
        hasSymbol: /[^A-Za-z0-9]/.test(p),
      };
    },
    isPasswordPolicyValid() {
      const pol = this.passwordPolicy;
      return pol.minLength && pol.hasUppercase && pol.hasLowercase && pol.hasNumber && pol.hasSymbol;
    },
    clientCode() {
      const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
      return userAuth?.user?.client_code || userAuth?.user?.company_code || '';
    },
  },
  mounted() {
    this.fetchUserProfile();
    this.getMaxSizeConfig();
  },
  methods: {
    extractCode(value) {
      if (!value) return '';
      if (value.includes(' - ')) {
        return value.split(' - ')[0].trim();
      }
      return value;
    },

    async fetchUserProfile() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api(this, authService.getNavbarProfile(), false);
        if (response && response.data) {
          this.userProfile = response.data;

          // Fetch department detail if exists
          let departmentDisplay = '';
          if (this.userProfile.department && this.userProfile.company_code) {
            try {
              const deptRes = await api(this, generalMasterDataService.list('department', { 
                company_code: this.userProfile.company_code,
                limit: 0 
              }), false);
              const dept = (deptRes.data || []).find(d => d.code === this.userProfile.department);
              departmentDisplay = dept ? `${dept.code} - ${dept.description}` : this.userProfile.department;
            } catch {
              departmentDisplay = this.userProfile.department;
            }
          }

          this.formData = {
            name: this.userProfile.name || '',
            email: this.userProfile.email || '',
            phone: this.userProfile.phone || '',
            department: departmentDisplay,
            section: this.getProfileSections(),
            sub_section: this.getProfileSubSections(),
            password: '',
            password_confirmation: ''
          };

          // Tampilkan nilai terpilih tanpa memanggil API (API baru dipanggil saat dropdown dibuka).
          this.formData.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
          this.formData.sub_section.forEach((v) => this.ensureSelectedOption(this.subSectionOptions, v));
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        this.error = "Failed to load profile information";
      } finally {
        this.loading = false;
      }
    },

    async searchDepartment(event) {
      const query = event.query;

      if (!this.userProfile.company_code) {
        this.departmentSuggestions = [];
        return;
      }

      try {
        const res = await api(this, generalMasterDataService.list('department', { 
          company_code: this.userProfile.company_code,
          limit: 0 
        }), false);
        const departments = (res.data || [])
          .map(d => `${d.code} - ${d.description}`)
          .filter(label => label.toLowerCase().includes(query.toLowerCase()));
        this.departmentSuggestions = departments;
      } catch (error) {
        console.error('Error loading departments:', error);
        this.departmentSuggestions = [];
      }
    },

    // Section/sub-section bisa lebih dari satu (relasi user_sections/user_sub_sections);
    // fallback ke kolom single `section`/`sub_section` untuk data lama.
    getProfileSections() {
      const p = this.userProfile || {};
      if (Array.isArray(p.sections) && p.sections.length) return p.sections.filter(Boolean);
      return p.section ? [p.section] : [];
    },
    getProfileSubSections() {
      const p = this.userProfile || {};
      if (Array.isArray(p.sub_sections) && p.sub_sections.length) return p.sub_sections.filter(Boolean);
      return p.sub_section ? [p.sub_section] : [];
    },
    isSameValues(a, b) {
      const x = [...(a || [])].sort();
      const y = [...(b || [])].sort();
      return x.length === y.length && x.every((v, i) => v === y[i]);
    },
    // FormData tidak bisa kirim array kosong; kirim key kosong supaya backend tahu isinya dikosongkan.
    appendArrayField(formData, key, values) {
      const list = (Array.isArray(values) ? values : []).filter(Boolean);
      if (!list.length) {
        formData.append(key, '');
        return;
      }
      list.forEach((v) => formData.append(`${key}[]`, v));
    },
    mapOptions(list, query = '') {
      const options = new Map();
      (Array.isArray(list) ? list : []).forEach((item) => {
        const code = item?.code || '';
        const label = item?.description || code;
        if (code && !options.has(code)) options.set(code, { label, value: code });
      });
      let result = [...options.values()];
      if (query) result = this.filterOptionsByQuery(result, query);
      return result;
    },
    filterOptionsByQuery(options, query) {
      const q = query.toLowerCase();
      return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    },
    // Pastikan nilai terpilih tetap tampil walau tidak ketemu di opsi (mis. dari profil user).
    ensureSelectedOption(options, value) {
      if (!value) return;
      if (options.some(o => o.value === value)) return;
      options.unshift({ label: value, value });
    },
    async fetchSectionOptions(search, limit) {
      this.sectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.sectionLoading = false;
      }
    },
    async fetchSubSectionOptions(search, limit) {
      this.subSectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('sub-section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.subSectionLoading = false;
      }
    },
    async loadSectionOptions() {
      const fetched = await this.fetchSectionOptions('', 10);
      this.sectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSectionOptions = [...this.sectionOptions];
      if (!Array.isArray(this.formData.section)) this.formData.section = [];
      this.formData.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
    },
    async loadSubSectionOptions() {
      const fetched = await this.fetchSubSectionOptions('', 10);
      this.subSectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSubSectionOptions = [...this.subSectionOptions];
      if (!Array.isArray(this.formData.sub_section)) this.formData.sub_section = [];
      this.formData.sub_section.forEach((v) => this.ensureSelectedOption(this.subSectionOptions, v));
    },
    // Search server-side: hit API begitu ketik min. 2 karakter.
    // Opsi terpilih (bisa lebih dari satu) wajib dipertahankan agar chip MultiSelect tidak hilang.
    async searchSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.formData.section || [];
      if (query.length < 2) {
        const base = [...this.allSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.sectionOptions = base;
        return;
      }
      let options = await this.fetchSectionOptions(query, 10);
      // Kalau backend abaikan param `search` / balik kosong, saring daftar yang sudah dimuat.
      if (!options.length) options = this.filterOptionsByQuery(this.allSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.sectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.sectionOptions = [...missingSelected, ...options];
    },
    async searchSubSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.formData.sub_section || [];
      if (query.length < 2) {
        const base = [...this.allSubSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.subSectionOptions = base;
        return;
      }
      let options = await this.fetchSubSectionOptions(query, 10);
      if (!options.length) options = this.filterOptionsByQuery(this.allSubSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.subSectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.subSectionOptions = [...missingSelected, ...options];
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > this.maxValidation.size) {
        this.errors.image = `Max Size ${this.maxValidation.size / 1000} KB`;
        this.selectedFile = null;
        this.previewUrl = null;
        return;
      }

      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        this.errors.image = 'Only PNG and JPEG files are allowed';
        this.selectedFile = null;
        this.previewUrl = null;
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
      delete this.errors.image;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },

    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword;
        this.$nextTick(() => {
          const input = this.$el.querySelector('input[name="password"]');
          if (input) input.type = this.showPassword ? 'text' : 'password';
        });
      } else if (field === 'password_confirmation') {
        this.showPasswordConfirmation = !this.showPasswordConfirmation;
        this.$nextTick(() => {
          const input = this.$el.querySelector('input[name="password_confirmation"]');
          if (input) input.type = this.showPasswordConfirmation ? 'text' : 'password';
        });
      }
    },

    async updateProfile() {
      this.updateLoading = true;
      this.errors = {};

      try {
        const formData = new FormData();
        formData.append('_method', 'PUT');
        
        if (this.formData.name !== this.userProfile.name) {
          formData.append('name', this.formData.name);
        }
        if (this.formData.email !== this.userProfile.email) {
          formData.append('email', this.formData.email);
        }
        if (this.formData.phone !== this.userProfile.phone) {
          formData.append('phone', this.formData.phone || '');
        }
        const sectionsChanged = !this.isSameValues(this.formData.section, this.getProfileSections());
        const subSectionsChanged = !this.isSameValues(this.formData.sub_section, this.getProfileSubSections());
        if (sectionsChanged) {
          this.appendArrayField(formData, 'sections', this.formData.section);
        }
        if (subSectionsChanged) {
          this.appendArrayField(formData, 'sub_sections', this.formData.sub_section);
        }

        if (this.formData.password && this.formData.password_confirmation) {
          if (!this.isPasswordPolicyValid) {
            this.errors.password = 'Password does not meet all requirements';
            return;
          }
          if (this.formData.password !== this.formData.password_confirmation) {
            this.errors.password_confirmation = 'Password confirmation does not match';
            return;
          }
          formData.append('password', this.formData.password);
          formData.append('password_confirmation', this.formData.password_confirmation);
        }

        if (this.selectedFile) {
          formData.append('photo', this.selectedFile);
        }

        const response = await api(this, authService.updateProfile(formData), true);

        if (response && response.data) {
          Object.assign(this.userProfile, response.data);
          // Response update tidak membawa relasi section; simpan nilai yang baru dikirim sebagai baseline.
          if (sectionsChanged) this.userProfile.sections = [...(this.formData.section || [])];
          if (subSectionsChanged) this.userProfile.sub_sections = [...(this.formData.sub_section || [])];
          this.formData.password = '';
          this.formData.password_confirmation = '';
          this.selectedFile = null;
          this.previewUrl = null;

          // Update localStorage user data
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          storedUser.name = response.data.name;
          storedUser.email = response.data.email;
          storedUser.phone = response.data.phone;
          if (response.data.image) {
            storedUser.image = response.data.image;
          }
          localStorage.setItem('user', JSON.stringify(storedUser));

          // Emit event to refresh navbar
          window.dispatchEvent(new Event('user-profile-updated'));
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        if (error.response?.status === 422) {
          const errors = error.response.data.errors;
          if (errors) {
            Object.keys(errors).forEach(key => {
              this.errors[key] = errors[key][0];
            });
          }
        } else if (error.response?.data?.code === 400) {
          error.response.data.data.forEach((v) => {
            this.errors[v.path] = v.msg;
          });
        }
      } finally {
        this.updateLoading = false;
      }
    },
    async getMaxSizeConfig() {
      try {
        const result = await setMaxSize();
        if (result) {
          this.maxValidation.size = result.data.data.maximum_file;
          this.maxValidation.text = `Max Size ${result.data.data.maximum_file / 1000} KB`;
        }
      } catch (error) {
        this.maxValidation.size = 2000000;
        this.maxValidation.text = `Max Size ${this.maxValidation.size / 1000} KB`;
      } 
    },
  }
};
</script>

<style scoped>
.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #344767;
  margin-bottom: 0.5rem;
}

.fw-semibold {
  font-weight: 600;
}

.text-xs {
  font-size: 0.75rem;
}

.password-input-wrapper {
  position: relative;
  width: 100%;
}

.password-input-wrapper .p-inputtext {
  width: 100%;
  padding-right: 2.5rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn:focus {
  outline: none;
}

.avatar-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.avatar-wrapper:hover {
  transform: scale(1.02);
}

.object-fit-cover {
  object-fit: cover;
}
</style>